/**
 * Thin fetch wrapper for the CCC data API.
 *
 * Adds the Cognito id_token to every request. Two namespaces: `accounts` and
 * `firms`, kept as separate exports for readability.
 */

import { config } from "./config";
import { getIdToken } from "./auth";

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

export interface Account {
  account_id: string;
  name: string;
  prop_firm: string;
  size: number;
  account_number: string;
  strategies_enabled: string[];
  active: boolean;
  created_at: string;
  updated_at: string;
}

export type AccountInput = Omit<Account, "account_id" | "created_at" | "updated_at">;

// ---------------------------------------------------------------------------
// Firms
// ---------------------------------------------------------------------------

export interface Firm {
  name: string;
  website: string;
  supports_tradelocker: boolean;
  active: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

/** Create / replace input. `name` only meaningful on create. */
export type FirmInput = Omit<Firm, "created_at" | "updated_at">;

/** Update patch — name is in the URL path, not the body. */
export type FirmPatch = Partial<Omit<FirmInput, "name">>;

// ---------------------------------------------------------------------------
// Plumbing
// ---------------------------------------------------------------------------

class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getIdToken();
  if (!token) throw new ApiError(401, "Not signed in");

  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? safeJsonParse(text) : undefined;

  if (!res.ok) {
    const message =
      (body && typeof body === "object" && "error" in body && typeof (body as { error: unknown }).error === "string"
        ? (body as { error: string }).error
        : `HTTP ${res.status}`);
    throw new ApiError(res.status, message, body);
  }

  return body as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// ---------------------------------------------------------------------------
// Account API
// ---------------------------------------------------------------------------

export const api = {
  list: () => request<{ items: Account[] }>("/accounts").then((r) => r.items),
  get: (id: string) => request<Account>(`/accounts/${encodeURIComponent(id)}`),
  create: (input: AccountInput) =>
    request<Account>("/accounts", { method: "POST", body: JSON.stringify(input) }),
  update: (id: string, patch: Partial<AccountInput>) =>
    request<Account>(`/accounts/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  remove: (id: string) =>
    request<{ deleted: string }>(`/accounts/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Firm API
// ---------------------------------------------------------------------------

export const firmsApi = {
  list: (opts?: { includeInactive?: boolean }) => {
    const qs = opts?.includeInactive ? "?include_inactive=true" : "";
    return request<{ items: Firm[] }>(`/firms${qs}`).then((r) => r.items);
  },
  get: (name: string) => request<Firm>(`/firms/${encodeURIComponent(name)}`),
  create: (input: FirmInput) =>
    request<Firm>("/firms", { method: "POST", body: JSON.stringify(input) }),
  update: (name: string, patch: FirmPatch) =>
    request<Firm>(`/firms/${encodeURIComponent(name)}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  deactivate: (name: string) =>
    request<{ deactivated: string; firm: Firm }>(
      `/firms/${encodeURIComponent(name)}`,
      { method: "DELETE" },
    ),
};

// ---------------------------------------------------------------------------
// Uploads / trade logs (v0.3 — ADR-0005)
// ---------------------------------------------------------------------------

export type UploadStatus =
  | "pending"
  | "parsing"
  | "parsed"
  | "parse_failed"
  | "no_parser";

export interface UploadStats {
  trade_count: number;
  wins: number;
  losses: number;
  ties: number;
  win_rate: number;
  gross_pnl: number;
  best_trade: { trade_id: string; pnl: number; symbol: string } | null;
  worst_trade: { trade_id: string; pnl: number; symbol: string } | null;
  by_symbol: Record<string, { n: number; pnl: number }>;
  by_strategy: Record<string, { n: number; pnl: number }>;
  by_weekday: { weekday: string; n: number; pnl: number }[];
  max_drawdown: number;
  period_start: string | null;
  period_end: string | null;
}

export interface Upload {
  upload_id: string;
  account_id: string;
  firm_name: string;
  filename: string;
  content_type: string;
  file_size: number;
  sha256: string;
  s3_key: string;
  status: UploadStatus;
  error?: string;
  trade_count?: number;
  stats?: UploadStats;
  narrative?: string;
  uploaded_at: string;
  parsed_at?: string;
}

export interface Trade {
  trade_id: string;
  upload_id: string;
  account_id: string;
  opened_at: string;
  closed_at?: string;
  symbol: string;
  side: "buy" | "sell";
  quantity: number;
  open_price: number;
  close_price?: number;
  pnl: number;
  commission?: number;
  strategy_id?: string;
}

interface PresignedPut {
  url: string;
  method: "PUT";
  headers: Record<string, string>;
  expires_in: number;
}

interface UploadInitResponse {
  upload: Upload;
  presigned: PresignedPut;
}

export const uploadsApi = {
  init: (body: {
    account_id: string;
    filename: string;
    content_type: string;
    file_size: number;
    sha256: string;
  }) =>
    request<UploadInitResponse>("/uploads/init", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  list: (opts?: { account_id?: string }) => {
    const qs = opts?.account_id ? `?account_id=${encodeURIComponent(opts.account_id)}` : "";
    return request<{ items: Upload[] }>(`/uploads${qs}`).then((r) => r.items);
  },
  get: (upload_id: string) => request<Upload>(`/uploads/${encodeURIComponent(upload_id)}`),
  trades: (upload_id: string) =>
    request<{ upload_id: string; items: Trade[] }>(`/uploads/${encodeURIComponent(upload_id)}/trades`),
  remove: (upload_id: string) =>
    request<{ deleted: string }>(`/uploads/${encodeURIComponent(upload_id)}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Manual trade log (v0.4)
// ---------------------------------------------------------------------------

export interface ManualTrade {
  trade_id: string;
  entry_date: string;
  prop_firm: string;
  account_id: string;
  instrument: string;
  side: "buy" | "sell";
  size: number;
  entry_price: number;
  stop_loss?: number | null;
  target_profit?: number | null;
  pnl: number;
  strategy?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export type ManualTradeInput = Omit<ManualTrade, "trade_id" | "created_at" | "updated_at">;
export type ManualTradePatch = Partial<ManualTradeInput>;

export const tradeLogApi = {
  list: (opts?: { account_id?: string }) => {
    const qs = opts?.account_id ? `?account_id=${encodeURIComponent(opts.account_id)}` : "";
    return request<{ items: ManualTrade[] }>(`/manual-trades${qs}`).then((r) => r.items);
  },
  get: (id: string) => request<ManualTrade>(`/manual-trades/${encodeURIComponent(id)}`),
  create: (input: ManualTradeInput) =>
    request<ManualTrade>("/manual-trades", { method: "POST", body: JSON.stringify(input) }),
  update: (id: string, patch: ManualTradePatch) =>
    request<ManualTrade>(`/manual-trades/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  remove: (id: string) =>
    request<{ deleted: string }>(`/manual-trades/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Payouts (v0.4)
// ---------------------------------------------------------------------------

export interface Payout {
  payout_id: string;
  date: string;
  prop_firm: string;
  account_id: string;
  amount: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export type PayoutInput = Omit<Payout, "payout_id" | "created_at" | "updated_at">;
export type PayoutPatch = Partial<PayoutInput>;

export const payoutsApi = {
  list: (opts?: { account_id?: string }) => {
    const qs = opts?.account_id ? `?account_id=${encodeURIComponent(opts.account_id)}` : "";
    return request<{ items: Payout[] }>(`/payouts${qs}`).then((r) => r.items);
  },
  get: (id: string) => request<Payout>(`/payouts/${encodeURIComponent(id)}`),
  create: (input: PayoutInput) =>
    request<Payout>("/payouts", { method: "POST", body: JSON.stringify(input) }),
  update: (id: string, patch: PayoutPatch) =>
    request<Payout>(`/payouts/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  remove: (id: string) =>
    request<{ deleted: string }>(`/payouts/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

/**
 * Compute the SHA-256 hex digest of a File using WebCrypto. Used as a hint
 * for duplicate detection on the server and as a sanity check.
 */
export async function sha256OfFile(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * PUT the file body directly to S3 using the presigned URL returned by
 * `uploadsApi.init`. Uses XHR (not fetch) because fetch doesn't surface
 * upload progress events in browsers.
 */
export function putToPresignedUrl(
  presigned: PresignedPut,
  file: File,
  onProgress?: (loaded: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(presigned.method, presigned.url, true);
    for (const [k, v] of Object.entries(presigned.headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 PUT failed: ${xhr.status} ${xhr.statusText} — ${xhr.responseText.slice(0, 200)}`));
    };
    xhr.onerror = () => reject(new Error("S3 PUT network error"));
    xhr.send(file);
  });
}

export { ApiError };
